<template>
	<template v-if="!contract">
		<app-input
			:model-value="values.contract_no"
			class="my-3"
			label="Contract No"
			:inline="true"
			:error="errors.contract_no"
			@update:model-value="updateContractNo"></app-input>
	</template>

	<list-group v-else :items="contractDetails" compact />
</template>

<script setup lang="ts">
	import { computed } from 'vue'
	import * as Yup from 'yup'
	import { useForm } from 'vee-validate'
	import ContractService from '@/services/contract'

	const contractApi = new ContractService()

	const props = defineProps({
		contract: {
			type: Object as () => {},
			default: undefined
		}
	})

	const { values, errors, setFieldValue, setFieldError, isFieldValid, validateField } = useForm<{
		contract_no: string
	}>({
		initialValues: {
			contract_no: ''
		},
		validationSchema: Yup.object({
			contract_no: Yup.string().test(
				'valid-contract-no',
				'Contract no must be 6 digits or in the format ABC/123456',
				(value) =>
					value === '' ||
					value === null ||
					value === undefined ||
					/^\d{6}$|^[A-Z]{3}\/\d{6}$/.test(value)
			)
		})
	})

	const emit = defineEmits(['update:contract', 'update:depot-distances'])

	const updateContractNo = async (contractNo: string) => {
		setFieldValue('contract_no', contractNo)
		await validateField('contract_no')
		if (isFieldValid('contract_no') && values.contract_no) {
			const contract_no = contractNo.slice(-6)
			findContract(contract_no)
		}
	}

	const findContract = async (value: string) => {
		contractApi
			.get(value)
			.then((result) => {
				if (result.data.data.length === 0) {
					setFieldError('contract_no', `Unable to find contract no ${value}`)
				} else {
					const contract = result.data.data
					emit('update:contract', contract)
				}
			})
			.catch((error) => {
				console.error('API error:', error)
				setFieldError('contract_no', `Unable to find contract no ${value}`)
			})
	}

	const contractDetails = computed(() => {
		const contract = props.contract

		const details = {
			contract_no: contract.contract_number,
			customer: contract.account_name,
			account: contract.account,
			delivery_address: deliveryAddress.value,
			ordered_by: contract.ordered_by
		}

		return details
	})

	const deliveryAddress = computed(() => {
		let address = []
		if (props.contract) {
			address = [
				props.contract.delivery_address_1,
				props.contract.delivery_address_2,
				props.contract.delivery_address_3,
				props.contract.delivery_postcode
			]
		}

		return address.filter((addressLine) => addressLine).join('<br>')
	})
</script>
